import type { PRICE_TEMPLATE_FIELD } from 'src/interfaces/Template.interface'
import type { PriceRuleFormValue } from '../components/hooks/usePriceModifierFields'
import {
  isRepricingActive,
  isValidNumericString,
} from './priceModifierValidation'
import { hasMeaningfulId } from './quicklistMoreMappingUtils'
import {
  applyPriceIsDefaultToPayload,
  resolvePriceRuleIsDefaultForSave,
  type PriceIsDefaultPayloadKey,
} from './priceIsDefaultApi'

export type { PriceIsDefaultFromApi, PriceIsDefaultPayloadKey } from './priceIsDefaultApi'
export { resolvePriceIsDefaultFromApi, resolvePriceRuleIsDefaultForSave, applyPriceIsDefaultToPayload } from './priceIsDefaultApi'

export type PriceModifierApiPayload = {
  id?: number
  name: string
  platform_id: number
  repricing: { type: 'PERCENTAGE' | 'FLAT'; value: number }
  isDefault?: boolean
  is_default?: boolean
  shipping_price: { mapped_field: number | null; value: number | null }
  source_platform_field_id: number | null
  destination_platform_field_id: number | null
  destination_platform_field_data_id: number | null
  value: number | null
  start_date?: string | null
  end_date?: string | null
}

const hasNonEmptyDateString = (value: string | undefined | null): boolean =>
  typeof value === 'string' && value.trim() !== ''

/** True when the user changed a dynamic price row away from generated defaults. */
export const hasPriceRuleUserValue = (rule: PriceRuleFormValue): boolean => {
  if (isRepricingActive(rule)) return true
  if (hasMeaningfulId(rule.mappedField)) return true
  if (isValidNumericString(rule.defaultValue)) return true
  if (isValidNumericString(rule.adjustmentValue)) return true
  if (
    rule.isPriceDate &&
    (hasNonEmptyDateString(rule.startDate) || hasNonEmptyDateString(rule.endDate))
  ) {
    return true
  }
  return false
}

/** True when a row was hydrated from an existing saved price-template rule. */
export const hasSavedPriceRuleId = (rule: PriceRuleFormValue): boolean =>
  hasMeaningfulId(rule.ruleId)

/**
 * Normalize cross-field price rule state before building save/update payloads.
 * Ensures cleared mapping/adjustment values do not leak into PUT bodies.
 */
export const sanitizePriceRuleFormValue = (rule: PriceRuleFormValue): PriceRuleFormValue => {
  const sanitized: PriceRuleFormValue = { ...rule }

  if (sanitized.mappingOption === 'DEFAULT') {
    sanitized.mappedField = ''
  } else {
    sanitized.defaultValue = ''
    if (!hasMeaningfulId(sanitized.mappedField)) {
      sanitized.mappedField = ''
    }
  }

  if (sanitized.adjustmentType === 'NO_CHANGE') {
    sanitized.adjustmentValue = ''
  } else if (!isValidNumericString(sanitized.adjustmentValue)) {
    sanitized.adjustmentValue = ''
  }

  if (!sanitized.isPriceDate) {
    sanitized.startDate = ''
    sanitized.endDate = ''
  } else {
    if (!hasNonEmptyDateString(sanitized.startDate)) sanitized.startDate = ''
    if (!hasNonEmptyDateString(sanitized.endDate)) sanitized.endDate = ''
  }

  return sanitized
}

export const sanitizePriceRuleFormValues = (
  rules: PriceRuleFormValue[]
): PriceRuleFormValue[] => rules.map(sanitizePriceRuleFormValue)

export type LegacyQuicklistRepricing = {
  type: string
  value: number | null
  /** Round-trip from GET price-template hydrate; not edited in listing profile UI. */
  isDefault?: boolean
  isDefaultPayloadKey?: PriceIsDefaultPayloadKey
}

/** User entered a legacy repricing amount (empty/null in UI = unset; explicit 0 counts). */
export const hasLegacyRepricingNumericInput = (
  value: number | null | undefined
): boolean => value != null && Number.isFinite(value)

/** Legacy fallback number input — empty when unset, magnitude when user entered a value (including 0). */
export const formatLegacyRepricingMagnitudeForInput = (value: number | null | undefined): string => {
  if (!hasLegacyRepricingNumericInput(value)) return ''
  return String(Math.abs(Number(value)))
}

/** True when legacy Quicklist price UI has user-provided repricing (including explicit zero). */
export const hasLegacyPriceUserValue = (repricing: LegacyQuicklistRepricing): boolean =>
  hasLegacyRepricingNumericInput(repricing.value)

/** Normalize legacy repricing state before building payloads. */
export const sanitizeLegacyRepricing = (
  repricing: LegacyQuicklistRepricing
): { type: 'PERCENTAGE' | 'FLAT'; value: number } => ({
  type: repricing.type === 'PERCENTAGE' ? 'PERCENTAGE' : 'FLAT',
  value: Number(repricing.value),
})

/** True when an existing price template should be updated on edit. */
export const shouldSendLegacyPriceUpdate = (
  repricing: LegacyQuicklistRepricing,
  mode: 'create' | 'update' | undefined
): boolean => mode === 'update' || hasLegacyPriceUserValue(repricing)

/** Create flow — only rows the user configured. */
export const buildPriceSaveEntries = (
  priceRules: PriceRuleFormValue[]
): PriceRuleFormValue[] =>
  sanitizePriceRuleFormValues(priceRules).filter(hasPriceRuleUserValue)

/**
 * Edit flow — include configured rows and previously saved rows being cleared.
 * Matches AddNewPriceTemplateModal update behaviour (all rows with ids must PUT).
 */
export const buildPriceModifierUpdateEntries = (
  priceRules: PriceRuleFormValue[]
): PriceRuleFormValue[] =>
  sanitizePriceRuleFormValues(priceRules).filter(
    (rule) => hasPriceRuleUserValue(rule) || hasSavedPriceRuleId(rule)
  )

export const buildPriceModifierPayloads = (
  rules: PriceRuleFormValue[],
  options: {
    ruleName: string
    platformId: number
    priceTemplateFields: PRICE_TEMPLATE_FIELD[]
  }
): PriceModifierApiPayload[] =>
  rules.map((rule) => {
    const adjustmentBase =
      rule.adjustmentType === 'NO_CHANGE'
        ? 0
        : isValidNumericString(rule.adjustmentValue)
          ? Number(rule.adjustmentValue)
          : 0
    const adjustmentValue =
      rule.adjustmentType === 'DECREASE' ? -Math.abs(adjustmentBase) : Math.abs(adjustmentBase)
    const priceField = options.priceTemplateFields.find((f) => f.id === rule.templateFieldId)
    const fieldAny = priceField as { id?: number } | undefined

    const sourcePlatformFieldId =
      rule.mappingOption !== 'DEFAULT' && hasMeaningfulId(rule.mappedField)
        ? Number(rule.mappedField)
        : null

    const defaultVal =
      rule.mappingOption === 'DEFAULT' && isValidNumericString(rule.defaultValue)
        ? Number(rule.defaultValue)
        : null

    const destinationPlatformFieldDataId =
      rule.destinationPlatformFieldDataId ?? fieldAny?.id ?? null

    const payload: PriceModifierApiPayload = {
      name: options.ruleName,
      platform_id: options.platformId,
      repricing: {
        type: rule.adjustmentMode,
        value: rule.adjustmentType === 'NO_CHANGE' ? 0 : adjustmentValue,
      },
      shipping_price: { mapped_field: null, value: null },
      source_platform_field_id: sourcePlatformFieldId,
      destination_platform_field_id: priceField?.platform_field || null,
      destination_platform_field_data_id: destinationPlatformFieldDataId,
      value: defaultVal,
    }

    if (rule.isPriceDate) {
      payload.start_date = hasNonEmptyDateString(rule.startDate) ? rule.startDate : null
      payload.end_date = hasNonEmptyDateString(rule.endDate) ? rule.endDate : null
    }

    if (hasSavedPriceRuleId(rule)) {
      payload.id = Number(rule.ruleId)
    }

    applyPriceIsDefaultToPayload(
      payload,
      resolvePriceRuleIsDefaultForSave(rule, priceField)
    )

    return payload
  })

export const buildLegacyPriceModifierPayload = (
  repricing: LegacyQuicklistRepricing,
  ruleName: string,
  platformId: number
): PriceModifierApiPayload => {
  const normalized = sanitizeLegacyRepricing(repricing)
  const payload: PriceModifierApiPayload = {
    name: ruleName,
    platform_id: platformId,
    repricing: normalized,
    shipping_price: { mapped_field: null, value: null },
    source_platform_field_id: null,
    destination_platform_field_id: null,
    destination_platform_field_data_id: null,
    value: null,
  }
  if (
    typeof repricing.isDefault === 'boolean' &&
    (repricing.isDefaultPayloadKey === 'is_default' ||
      repricing.isDefaultPayloadKey === 'isDefault')
  ) {
    applyPriceIsDefaultToPayload(payload, {
      value: repricing.isDefault,
      payloadKey: repricing.isDefaultPayloadKey,
    })
  }
  return payload
}

export const resolveQuicklistPricePayload = (options: {
  priceTemplateFields: PRICE_TEMPLATE_FIELD[]
  priceRules: PriceRuleFormValue[]
  repricing: LegacyQuicklistRepricing
  ruleName: string
  platformId: number | undefined | null
  mode?: 'create' | 'update'
}): PriceModifierApiPayload | PriceModifierApiPayload[] | null => {
  const platformId = Number(options.platformId)
  if (!Number.isFinite(platformId)) return null

  if (options.priceTemplateFields.length > 0) {
    const saveEntries =
      options.mode === 'update'
        ? buildPriceModifierUpdateEntries(options.priceRules)
        : buildPriceSaveEntries(options.priceRules)
    if (!saveEntries.length) return null

    return buildPriceModifierPayloads(saveEntries, {
      ruleName: options.ruleName,
      platformId,
      priceTemplateFields: options.priceTemplateFields,
    })
  }

  if (!shouldSendLegacyPriceUpdate(options.repricing, options.mode)) return null

  return buildLegacyPriceModifierPayload(
    options.repricing,
    options.ruleName,
    platformId
  )
}
